import { Fragment, FC } from "react";
import {
  Flex,
  Box,
  IconButton,
  Heading,
  Text,
  VStack,
  Image,
} from "@chakra-ui/react";
import { ShiIcon } from "src/atoms";
import { useLocation } from "@reach/router";

import { ReferralComponentProps } from "./types";
import ShiButton from "src/atoms/ShiButton";
import { Link as GatsbyLink } from "gatsby";
import handleWebAppRedirection from "src/atoms/PrismicLink/lib/handleWebAppRedirection";
import useVisitorContext from "src/providers/VisitorContext/useVisitorContext";
import { ClickInfo } from "src/lib/event/types";
import { buildNewAccountClickedEvent } from "src/lib/event";
import { logEvent } from "src/lib/analytics";

const ReferralComponent: FC<ReferralComponentProps> = ({ code }) => {
  if (!code) {
    throw new Error(`Shouldn't be rendered if code is undefined`);
  }

  const visitorContext = useVisitorContext();
  const location = useLocation();
  const label = "Ouvrir mon compte";

  type ImageSize = {
    width: string;
    height: string;
  };

  const imageSizes: ImageSize[] = [
    { width: "320", height: "233" },
    { width: "640", height: "466" },
    { width: "960", height: "700" },
    { width: "1024", height: "1024" },
    { width: "2048", height: "2048" },
    { width: "3072", height: "3072" },
    { width: "4096", height: "4096" },
  ];

  const assetUrl =
    "https://images.prismic.io/shine/25be81e0-59e4-4a0a-8be6-b8cc75c58efc_card.jpg";

  return (
    <Flex
      flexDirection={{ base: "column", md: "row" }}
      minHeight="100vh"
      minWidth={{ base: "auto", md: "100vw" }}
      maxWidth={{ base: "480px", md: "auto" }}
      backgroundColor={{ base: "white", md: "yellow-0" }}
      padding={{ base: "space-16", md: "0" }}
      gap={{ base: "space-16", md: "0" }}
      margin="0 auto"
    >
      <Box
        flexGrow={{ base: 0, md: 1 }}
        textAlign="center"
        alignSelf="center"
        width={{ base: "100%", md: "50%" }}
      >
        <Flex
          flexDirection="column"
          textAlign="left"
          padding={{ base: "0", md: "space-80" }}
          justifyContent="center"
          alignItems="center"
        >
          <Box>
            <IconButton
              as={GatsbyLink}
              aria-label="Shine logo"
              variant="link"
              padding="6px"
              marginBottom="space-24"
              icon={<ShiIcon name="ShineLogo" width="96px" height="auto" />}
              to="/"
            />

            <VStack
              spacing="space-16"
              align="flex-start"
              backgroundColor={{ base: "yellow-0", md: "transparent" }}
              boxSizing="border-box"
              padding={{ base: "space-24", md: "space-0" }}
              borderRadius={{ base: "16px", md: "0" }}
            >
              <Fragment>
                <Flex direction="column" gap="24px" alignItems="flex-start">
                  <Heading>Cette offre n&apos;est plus disponible</Heading>

                  <Text>
                    Votre lien de parrainage est expiré. Demandez-en un nouveau
                    à votre parrain. Vous pouvez aussi ouvrir un compte dès
                    maintenant et découvrir nos offres.
                  </Text>

                  <Flex
                    direction={{ base: "column", md: "row" }}
                    alignItems="center"
                    gap="space-14"
                    width={{ base: "100%", md: "auto" }}
                  >
                    <ShiButton
                      onClick={(e) => {
                        e.preventDefault();

                        const clickInfo: ClickInfo = {
                          label,
                          fromLocation: location,
                        };

                        const link = handleWebAppRedirection(visitorContext, {
                          from: location,
                        });
                        const event = buildNewAccountClickedEvent(
                          link,
                          clickInfo,
                        );

                        logEvent(event, () => window.location.assign(link));
                      }}
                      width={{ base: "100%", md: "auto" }}
                    >
                      {label}
                    </ShiButton>

                    <ShiButton
                      as={GatsbyLink}
                      variant="primary"
                      to="/"
                      hasArrow={false}
                      width={{ base: "100%", md: "auto" }}
                      backgroundColor="yellow-6"
                      color="grey-0"
                      _hover={{ backgroundColor: "rgba(255, 248, 210, 0.6)" }}
                    >
                      Découvrir nos offres
                    </ShiButton>
                  </Flex>
                </Flex>
              </Fragment>
            </VStack>
          </Box>
        </Flex>
      </Box>
      <Box
        flexGrow={{ base: 0, md: 1 }}
        width={{ base: "100%", md: "50%" }}
        height={{ base: "250px", md: "auto" }}
        minHeight={{ base: 0, md: "100%" }}
        borderRadius={{ base: "16px", md: "0" }}
        overflow="hidden"
      >
        <Image
          width="100%"
          height="100%"
          fit="cover"
          srcSet={imageSizes
            .map((size: ImageSize) => {
              const { height, width } = size;
              const rectangularSuffix = width === height ? ",0,1536,1536" : "";
              return `${assetUrl}?auto=compress,format${rectangularSuffix}&w=${width}&h=${height} ${width}w`;
            })
            .join(", ")}
          sizes={imageSizes
            .map(({ width }, index) => {
              const isLast = index === imageSizes.length - 1;
              return isLast
                ? `${width}px`
                : `(max-width: ${width}px) ${width}px`;
            })
            .join(", ")}
        />
      </Box>
    </Flex>
  );
};

export default ReferralComponent;
